import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { setToken, removeToken, getToken } from '@/utils/auth'

/**
 * 用户状态管理 Store (Pinia)
 * 使用 Composition API 风格
 */
export const useUserStore = defineStore('user', () => {
  // 状态 (State)
  const token = ref(null)
  const userInfo = ref(null)
  const loginData = ref(null)
  const loginTime = ref(null)
  const timeoutTimer = ref(null)
  const permissions = ref([])
  const allowedRoutes = ref([])

  // 计算属性 (Getters)
  const isLoggedIn = computed(() => {
    // 根据当前URL判断是否为管理员页面
    const isAdminPage = window.location.href.includes('/admin')
    // 如果token为null，则检查localStorage中是否有对应类型的token
    return !!token.value || !!getToken(isAdminPage ? 'admin' : 'user')
  })

  const getUserInfo = computed(() => {
    return userInfo.value || {}
  })

  const getLoginData = computed(() => {
    // 如果state中没有loginData，尝试从localStorage获取
    if (!loginData.value) {
      const storedData = localStorage.getItem('loginData')
      return storedData ? JSON.parse(storedData) : null
    }
    return loginData.value
  })
  /**
   * 获取权限列表（操作级）
   * @returns {string[]} 当前用户的操作权限枚举列表
   */
  const getPermissions = computed(() => {
    return Array.isArray(permissions.value) ? permissions.value : []
  })
  /**
   * 获取允许访问的路由列表（页面级）
   * @returns {string[]} 路由名称列表，控制页面可访问性与侧边栏可见性
   */
  const getAllowedRoutes = computed(() => {
    return Array.isArray(allowedRoutes.value) ? allowedRoutes.value : []
  })
  /**
   * 校验是否拥有某个操作权限
   * @param {string} perm 操作权限键（如 'websiteInfo.addDoc'）
   * @returns {boolean} 是否拥有该权限；当列表为空时默认放行
   */
  const hasPermission = (perm) => {
    if (!perm) return true
    const list = getPermissions.value
    if (!list || list.length === 0) return true
    return list.includes(perm)
  }
  /**
   * 校验是否拥有一组操作权限（全部满足）
   * @param {string[]} perms 操作权限键数组
   * @returns {boolean} 是否全部拥有；当列表为空时默认放行
   */
  const hasAllPermissions = (perms) => {
    if (!Array.isArray(perms) || perms.length === 0) return true
    const set = new Set(getPermissions.value)
    if (set.size === 0) return true
    return perms.every(p => set.has(p))
  }
  /**
   * 校验是否允许访问某个页面路由
   * @param {string} routeName 路由名称（如 'CourseManagement'）
   * @returns {boolean} 是否允许访问；当列表为空时默认放行
   */
  const hasRouteAccess = (routeName) => {
    if (!routeName) return true
    const routes = getAllowedRoutes.value
    if (routes.length === 0) return true
    return routes.includes(routeName)
  }

  // 动作 (Actions)
  /**
   * 初始化用户状态
   * 从localStorage恢复数据
   */
  const initializeUserState = () => {
    try {
      // 根据当前URL判断token类型
      let tokenType = 'user'
      if (window.location.href.includes('/admin')) {
        tokenType = 'admin'
      } else if (window.location.href.includes('/student')) {
        tokenType = 'student'
      } else if (window.location.href.includes('/teacher')) {
        tokenType = 'teacher'
      }
      
      const storedToken = getToken(tokenType)
      
      if (storedToken) {
        token.value = storedToken
      }

      // 恢复用户信息
      const userInfoStr = localStorage.getItem('userInfo')
      if (userInfoStr) {
        userInfo.value = JSON.parse(userInfoStr)
      }

      // 恢复登录数据
      const loginDataStr = localStorage.getItem('loginData')
      if (loginDataStr) {
        loginData.value = JSON.parse(loginDataStr)
      }
      const permsStr = localStorage.getItem('permissions')
      if (permsStr) {
        try {
          const parsed = JSON.parse(permsStr)
          if (Array.isArray(parsed)) {
            permissions.value = parsed
          }
        } catch (e) {
          console.warn('解析权限失败', e)
        }
      }
      const routesStr = localStorage.getItem('allowedRoutes')
      if (routesStr) {
        try {
          const parsed = JSON.parse(routesStr)
          if (Array.isArray(parsed)) {
            allowedRoutes.value = parsed.includes('TeacherHome') ? parsed : [...parsed, 'TeacherHome']
          }
        } catch (e) {
          console.warn('解析允许路由失败', e)
        }
      }

      // 恢复登录时间
      const savedLoginTime = localStorage.getItem('loginTime')
      if (savedLoginTime) {
        loginTime.value = savedLoginTime
        
        // 检查是否超时
        if (checkLoginTimeout()) {
          console.log('检测到登录已超时，清理状态')
          clearUserState()
          return
        }
        
        // 如果有有效的登录状态，启动超时检查（仅对教师和管理员）
        if (token.value) {
          // 检查用户类型，只有教师和管理员才启动超时检查
          const storedLoginData = localStorage.getItem('loginData')
          if (storedLoginData) {
            const parsedLoginData = JSON.parse(storedLoginData)
            const userType = parsedLoginData?.userType
            if (userType === 'teacher' || userType === 'admin') {
              startTimeoutTimer()
            }
          }
        }
      }
      if (permissions.value && permissions.value.length) {
        const computed = computeAllowedRoutes(permissions.value)
        if (computed.length) {
          const merged = Array.from(new Set([...(allowedRoutes.value || []), ...computed]))
          allowedRoutes.value = merged.includes('TeacherHome') ? merged : ['TeacherHome', ...merged]
          localStorage.setItem('allowedRoutes', JSON.stringify(allowedRoutes.value))
        }
      }
    } catch (error) {
      console.error('初始化用户状态失败:', error)
      // 清理可能损坏的数据
      clearUserState()
    }
  }

  /**
   * 设置Token
   * @param {string} newToken - 新的token
   */
  const setUserToken = (newToken) => {
    token.value = newToken
  }

  /**
   * 设置用户信息
   * @param {Object} info - 用户信息对象
   */
  const setUserInfo = (info) => {
    userInfo.value = info
    // 保存用户信息到localStorage
    if (info) {
      localStorage.setItem('userInfo', JSON.stringify(info))
    } else {
      localStorage.removeItem('userInfo')
    }
  }

  /**
   * 设置登录数据
   * @param {Object} data - 完整的登录响应数据
   */
  const setLoginData = (data) => {
    loginData.value = data
    // 保存完整登录数据到localStorage
    if (data) {
      localStorage.setItem('loginData', JSON.stringify(data))
    } else {
      localStorage.removeItem('loginData')
    }
  }

  /**
   * 设置并持久化操作权限列表
   * @param {string[]} list 权限键数组（后端返回的权限集合）
   */
  const setPermissions = (list) => {
    const arr = Array.isArray(list) ? list : []
    permissions.value = arr
    localStorage.setItem('permissions', JSON.stringify(arr))
  }
  /**
   * 设置并持久化允许访问的路由列表
   * @param {string[]} list 路由名称集合（用于导航与侧边栏控制）
   */
  const setAllowedRoutes = (list) => {
    const arr = Array.isArray(list) ? list : []
    const next = arr.includes('TeacherHome') ? arr : ['TeacherHome', ...arr]
    allowedRoutes.value = next
    localStorage.setItem('allowedRoutes', JSON.stringify(next))
  }
  /**
   * 基于操作权限推导允许访问的路由列表
   * @param {string[]} permsList 操作权限集合（后端返回的 'list'）
   * @returns {string[]} 路由名称列表
   */
  const computeAllowedRoutes = (permsList) => {
    const perms = Array.isArray(permsList) ? permsList : []
    const permsLower = new Set(perms.map(p => String(p || '').toLowerCase()))
    const any = (arr) => arr.some(p => permsLower.has(String(p || '').toLowerCase()))
    const result = new Set(['TeacherHome'])

    if (any(['course.index'])) {
      result.add('CourseManagement')
      result.add('CourseManagementDetail')
      result.add('CourseDiscussionDetails')
      result.add('CourseDiscussionScoreStats')
      result.add('CourseTemplateTeacher')
      result.add('CourseTemplateForm')
      result.add('TeacherOpenCourse')
      result.add('TeacherOpenCoursePeriodDetail')
      result.add('OpenVideoPlayer')
      result.add('OpenMaterials')
      result.add('OpenExternalLinks')
      result.add('CourseClassManagement')
      result.add('VideoPlayer')
      result.add('Materials')
      result.add('ExternalLinks')
      result.add('WorkExamManage')
      result.add('WorkExamPreview')
      result.add('HomeworkRecord')
    }
    if (any(['course_class.index'])) result.add('EnrollmentRecords')
    if (any(['coursetotal.index', 'course_total.index'])) result.add('CourseStatistics')

    if (any(['question.index'])) {
      result.add('QuestionManagement')
      result.add('ImportErrorAnalysis')
    }
    if (any(['paper.index'])) {
      result.add('PaperManagement')
      result.add('PaperDetail')
    }

    if (any(['happy_circle.index'])) result.add('HappyCircle')
    if (any(['notice.index', 'course_notice.push'])) result.add('MessagePush')
    if (any(['teaching_news.index', 'announcement.index'])) result.add('TeacherNews')

    if (any(['manage.account'])) result.add('TeacherPersonalInformation')
    if (any(['manage.password'])) result.add('TeacherPasswordChanged')
    if (any(['manage.index'])) result.add('TeacherAdminManagement')
    if (any(['role.index'])) {
      result.add('TeacherRoleManagement')
      result.add('TeacherRolePermissionSetting')
    }
    if (any(['role.setting'])) result.add('TeacherRolePermissionSetting')

    if (any(['category.index'])) result.add('TeacherSubjectCategory')
    if (any(['school_banner.index'])) result.add('TeacherBannerManagement')
    if (any(['school_column.index'])) result.add('TeacherHomepageColumn')
    if (any(['report.index'])) result.add('TeacherStatisticalReport')

    if (any(['college.index'])) result.add('CollegeManagement')
    if (any(['classes.index'])) result.add('ClassManagement')
    if (any(['student.index'])) {
      result.add('StudentDatabase')
      result.add('StudentManagement')
    }

    if (any(['course_results.index'])) result.add('CourseGrades')
    if (any(['study_record.index'])) result.add('OnlineStudyRecord')
    if (any(['course_dis_data.index'])) result.add('TopicDiscussionStats')
    if (any(['course_node_discuss.index'])) result.add('VideoDiscussionStats')
    if (any(['course_video_top.index'])) result.add('VideoRankingStats')
    if (any(['course_work.index'])) result.add('WorkSituation')
    if (any(['course_exam.index'])) result.add('ReportPage')
    if (any(['course_report.index'])) result.add('OverallReport')
    if (any(['course_video_report.index'])) result.add('VideoReport')

    return Array.from(result)
  }

  /**
   * 清理用户状态
   */
  const clearUserState = () => {
    token.value = null
    userInfo.value = null
    loginData.value = null
    loginTime.value = null
    permissions.value = []
    allowedRoutes.value = []
    
    // 停止超时检查定时器
    stopTimeoutTimer()
    
    // 清理localStorage
    localStorage.removeItem('userInfo')
    localStorage.removeItem('loginData')
    localStorage.removeItem('loginTime')
    localStorage.removeItem('permissions')
    localStorage.removeItem('allowedRoutes')
  }

  /**
   * 用户登录
   * @param {Object} params - 登录参数
   * @param {string} params.token - 登录token
   * @param {Object} params.userInfo - 用户信息
   * @param {Object} params.loginData - 完整登录数据
   */
  const login = async ({ token: loginToken, userInfo: loginUserInfo, loginData: loginResponseData }) => {
    try {
      // 从loginUserInfo中获取userType
      const userType = loginUserInfo?.userType || (window.location.href.includes('/admin') ? 'admin' : 'user')
      
      // 提取token字符串（如果传入的是对象）
      let tokenString = loginToken
      if (typeof loginToken === 'object') {
        tokenString = loginToken.token || JSON.stringify(loginToken)
      }
      
      // 根据用户类型存储token
      const tokenType = userType === 'admin' ? 'admin' : (userType === 'student' ? 'student' : 'teacher')
      setToken(tokenString, tokenType) // 存储为对应类型的token
      setUserToken(tokenString)
      
      console.log(`已存储${tokenType}类型token:`, tokenString)
      
      // 判断是否为管理员登录
      const isAdmin = window.location.href.includes('/admin')
      
      console.log('登录类型:', tokenType, '是否为管理员页面:', isAdmin)
      
      // 持久化存储token，同时存储到两种类型
      // setToken(loginToken, 'admin') // 存储为管理员token
      // setToken(loginToken, 'user')  // 同时也存储为用户token
      // setUserToken(loginToken)

      //判断角色类型再进行token存储
      if(tokenType == 'admin'){
        setToken(loginToken, 'admin') // 存储为管理员token
      }else{
        setToken(loginToken, 'user')  // 同时也存储为用户token
      }
      setUserToken(loginToken)
      
      // 如果有userInfo，则存储
      if (loginUserInfo) {
        setUserInfo(loginUserInfo)
      } else {
        setUserInfo({ role: userType, userType })
      }
      
      // 存储完整的登录响应数据
      if (loginResponseData) {
        const normalizedLoginData = {
          ...loginResponseData,
          userType: loginResponseData?.userType || userType,
          type: loginResponseData?.type || userType
        }
        setLoginData(normalizedLoginData)
        const perms = Array.isArray(loginResponseData.permissions) ? loginResponseData.permissions : (Array.isArray(loginResponseData.list) ? loginResponseData.list : [])
        setPermissions(perms)
        setAllowedRoutes(computeAllowedRoutes(perms))
      }
      
      // 登录成功后优先从缓存获取学校列表
      try {
        const { useSchoolStore } = await import('./school')
        const schoolStore = useSchoolStore()
        // 首先尝试从缓存加载
        if (!schoolStore.loadFromLocalStorage()) {
          // 缓存无效时从服务器获取
          await schoolStore.fetchSchoolList()
          console.log('登录后从服务器获取学校列表成功')
        } else {
          console.log('登录后从缓存加载学校列表成功')
        }
      } catch (schoolError) {
        console.warn('登录后获取学校列表失败:', schoolError)
        // 不影响登录流程，只是警告
      }
      
      // 更新登录时间
      updateLoginTime()
      
      console.log('登录成功，用户状态已更新')
      return true
    } catch (error) {
      console.error('登录失败:', error)
      return false
    }
  }

  /**
   * 获取用户信息
   * 从API获取最新的用户信息
   */
  const fetchUserInfo = async () => {
    try {
      console.log('获取用户信息')
      
      // 获取当前用户类型（从loginData或token类型判断）
      const loginDataType = loginData.value?.type || getLoginDataType()
      
      // 优先根据登录类型判断，而不是仅依赖URL
      if (loginDataType === 'teacher' || 
          window.location.href.includes('/teacher') || 
          (window.location.href.includes('/teacher/login'))) {
        // 教师页面 - 使用教师API
        const { getTeacherInfo } = await import('@/api/teacherapi/individualRelated')
        const res = await getTeacherInfo()
        
        if (res.code === 200 && res.data) {
          // 更新用户信息
          setUserInfo(res.data)
          console.log('获取教师信息成功:', res.data)
          return res.data
        } else {
          console.warn('获取教师信息失败:', res)
          return null
        }
      } else if (loginDataType === 'student' || 
                 window.location.href.includes('/student') || 
                 (window.location.href.includes('/student/login'))) {
        // 学生页面 - 使用学生API
        try {
          const { getStudentInfo } = await import('@/api/studentapi/student')
          const res = await getStudentInfo()
          
          if (res.code === 200 && res.data) {
            // 更新用户信息
            setUserInfo(res.data)
            console.log('获取学生信息成功:', res.data)
            return res.data
          } else {
            console.warn('获取学生信息失败:', res)
            return null
          }
        } catch (studentError) {
          console.warn('学生信息API可能未实现:', studentError)
          // 如果学生API未实现，返回null但不影响流程
          return null
        }
      } else {
        // 管理员页面 - 使用管理员API
        const { getUserInfo: getUserInfoAPI } = await import('@/api/adminapi/admin')
        const res = await getUserInfoAPI()
        
        console.log('获取用户信息结果:', res)
        
        if (res.code === 200 && res.data) {
          // 更新用户信息
          setUserInfo(res.data)
          return res.data
        } else {
          console.error('获取用户信息失败:', res.data || '未知错误')
          return null
        }
      }
    } catch (error) {
      console.error('获取用户信息出错:', error)
      return null
    }
  }

  // 辅助函数：从token或本地存储判断登录类型
  const getLoginDataType = () => {
    // 尝试从localStorage中的token类型判断
    const tokenKeys = ['student', 'teacher', 'admin']
    for (const key of tokenKeys) {
      if (localStorage.getItem(`token-${key}`)) {
        return key
      }
    }
    return null
  }

  /**
   * 用户登出
   */
  const logout = () => {
    try {
      console.log('Pinia登出操作')
      
      // 停止超时检查定时器
      stopTimeoutTimer()
      
      // 移除所有类型的token
      removeToken('admin')
      removeToken('user')
      removeToken('student')
      removeToken('teacher')
      
      // 清理用户状态
      clearUserState()
      
      console.log('登出成功，用户状态已清理')
      return true
    } catch (error) {
      console.error('登出失败:', error)
      return false
    }
  }

  /**
   * 更新用户信息
   * @param {Object} updates - 要更新的用户信息字段
   */
  const updateUserInfo = (updates) => {
    if (userInfo.value) {
      userInfo.value = { ...userInfo.value, ...updates }
      localStorage.setItem('userInfo', JSON.stringify(userInfo.value))
    }
  }

  /**
   * 检查token是否有效
   * @returns {boolean} token是否有效
   */
  const isTokenValid = () => {
    const currentToken = token.value || getToken('admin') || getToken('user')
    return !!currentToken
  }

  /**
   * 获取当前token
   * @returns {string|null} 当前token
   */
  const getCurrentToken = () => {
    return token.value || getToken('admin') || getToken('user')
  }

  /**
   * 强制更新状态
   * 重新从localStorage加载用户状态
   */
  const forceUpdate = () => {
    initializeUserState()
  }

  /**
   * 检查登录是否超时
   * 学生用户永不超时，只有教师和管理员才检查超时
   * @returns {boolean} 是否超时
   */
  const checkLoginTimeout = () => {
    const ld = getLoginData.value
    const ui = getUserInfo.value
    const userType = ld?.userType || ld?.type || ui?.userType || ui?.type
    if (userType === 'student') {
      return false
    }
    const storedAdminTime = localStorage.getItem('adminLoginTime')
    const storedStudentTime = localStorage.getItem('studentLoginTime')
    const baseTime = userType === 'student' ? storedStudentTime : storedAdminTime || localStorage.getItem('loginTime') || loginTime.value
    if (!baseTime) {
      return true
    }
    if (userType === 'teacher' || userType === 'admin') {
      const currentTime = new Date().getTime()
      const loginTimestamp = new Date(baseTime).getTime()
      const timeoutDuration = 2 * 60 * 60 * 1000
      return (currentTime - loginTimestamp) > timeoutDuration
    }
    return true
  }

  /**
   * 启动超时检查定时器
   */
  const startTimeoutTimer = () => {
    // 清除现有定时器
    if (timeoutTimer.value) {
      clearInterval(timeoutTimer.value)
    }
    
    // 每分钟检查一次是否超时
    timeoutTimer.value = setInterval(() => {
      if (checkLoginTimeout()) {
        console.log('登录超时，自动退出')
        autoLogout()
      }
    }, 60 * 1000) // 每分钟检查一次
  }

  /**
   * 停止超时检查定时器
   */
  const stopTimeoutTimer = () => {
    if (timeoutTimer.value) {
      clearInterval(timeoutTimer.value)
      timeoutTimer.value = null
    }
  }

  /**
   * 自动退出登录
   */
  const autoLogout = () => {
    console.log('执行自动退出登录')
    
    // 停止定时器
    stopTimeoutTimer()
    
    // 清理用户状态
    logout()
    
    // 可以在这里添加提示消息或跳转到登录页面
    // 注意：在store中不能直接使用ElMessage或router，需要在组件中监听状态变化
    
    // 触发自定义事件通知组件
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('userTimeout', { 
        detail: { message: '登录已超时，请重新登录' } 
      }))
    }
  }

  /**
   * 更新登录时间
   */
  const updateLoginTime = () => {
    const currentTime = new Date().toISOString()
    loginTime.value = currentTime
    
    // 设置通用登录时间（向后兼容）
    localStorage.setItem('loginTime', currentTime)
    
    // 根据当前用户类型设置专门的登录时间
    const currentUserInfo = getUserInfo.value
    const userType = currentUserInfo?.userType || currentUserInfo?.type
    
    if (userType === 'teacher' || userType === 'admin') {
      localStorage.setItem('adminLoginTime', currentTime)
      console.log('已设置教师/管理员登录时间:', currentTime)
      // 只对教师和管理员启动超时检查
      startTimeoutTimer()
    } else if (userType === 'student') {
      localStorage.setItem('studentLoginTime', currentTime)
      console.log('已设置学生登录时间:', currentTime)
      // 学生不启动超时检查
    }
  }

  // 初始化状态
  initializeUserState()

  // 返回状态和方法
  return {
    // 状态
    token,
    userInfo,
    loginData,
    loginTime,
    permissions,
    allowedRoutes,
    
    // 计算属性
    isLoggedIn,
    getUserInfo,
    getLoginData,
    getPermissions,
    getAllowedRoutes,
    
    // 方法
    initializeUserState,
    setUserToken,
    setUserInfo,
    setLoginData,
    setPermissions,
    setAllowedRoutes,
    clearUserState,
    fetchUserInfo,
    login,
    logout,
    updateUserInfo,
    isTokenValid,
    getCurrentToken,
    forceUpdate,
    checkLoginTimeout,
    startTimeoutTimer,
    stopTimeoutTimer,
    autoLogout,
    updateLoginTime,
    hasPermission,
    hasAllPermissions,
    hasRouteAccess,
    computeAllowedRoutes
  }
}, {
  // Pinia 持久化配置（可选）
  persist: {
    key: 'user-store',
    storage: localStorage,
    paths: ['token', 'userInfo', 'loginData']
  }
})

// 默认导出（兼容性）
export default useUserStore
